import { useRef, useState } from 'react';
import { Image as ImageIcon, X, Upload } from 'lucide-react';

/**
 * DocumentsSection — upload de fotos (pré-envio).
 * Porte do apps/prospect-web/src/components/DocumentsSection.tsx.
 */

export interface PendingPhoto {
  file: File;
  preview: string;
  descricao: string;
}

interface Props {
  pending: PendingPhoto[];
  onChange: (photos: PendingPhoto[]) => void;
}

export default function DocumentsSection({ pending, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  function handleFiles(files: FileList | null): void {
    if (!files) return;
    const novos: PendingPhoto[] = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (!f || !f.type.startsWith('image/')) continue;
      novos.push({
        file: f,
        preview: URL.createObjectURL(f),
        descricao: '',
      });
    }
    onChange([...pending, ...novos]);
  }

  function updateDescricao(idx: number, descricao: string): void {
    const copy = [...pending];
    const target = copy[idx];
    if (!target) return;
    copy[idx] = { ...target, descricao };
    onChange(copy);
  }

  function remove(idx: number): void {
    const target = pending[idx];
    if (target) URL.revokeObjectURL(target.preview);
    onChange(pending.filter((_, i) => i !== idx));
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <ImageIcon size={16} className="text-primary" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-primary">
          Documentos ({pending.length})
        </h3>
      </div>

      {pending.length > 0 && (
        <div className="space-y-2 mb-3">
          {pending.map((p, i) => (
            <div key={i} className="bg-canvas border border-line rounded-xl p-3 flex gap-3">
              <img
                src={p.preview}
                alt=""
                className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start mb-1.5">
                  <p className="text-xs text-fg-2 truncate">{p.file.name}</p>
                  <button
                    onClick={() => remove(i)}
                    className="text-muted hover:text-danger flex-shrink-0 ml-2"
                  >
                    <X size={16} />
                  </button>
                </div>
                <input
                  value={p.descricao}
                  onChange={(e) => updateDescricao(i, e.target.value)}
                  placeholder="Descrição (ex: Foto do RG)"
                  className="w-full bg-surface border border-line rounded-lg px-2.5 h-9 text-fg text-xs outline-none focus:border-primary placeholder:text-muted"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition ${
          dragOver
            ? 'border-primary bg-primary/10'
            : 'border-line bg-canvas/50 hover:bg-canvas'
        }`}
      >
        <Upload size={24} className="mx-auto text-fg-2 mb-2" />
        <p className="text-sm text-fg-2">Clique ou arraste imagens</p>
        <p className="text-xs text-muted mt-0.5">JPG, PNG — serão comprimidas no envio</p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {pending.length > 0 && (
        <p className="text-xs text-muted mt-2">
          {pending.length} foto(s) será(ão) anexada(s) após salvar.
        </p>
      )}
    </div>
  );
}
